/**
 * hvacSystems.js — System-Based HVAC data model & registry.
 *
 * The user thinks "I have N HVAC systems, each serving certain zones" — not
 * "200 EnergyPlus objects". One registry drives three layers:
 *   1. the Add-System wizard (which inputs to show per type),
 *   2. validation (engine/hvacValidation.js),
 *   3. IDF emission via HVACTemplate:* (engine/hvacGenerator.js).
 *
 * We deliberately emit HVACTemplate:* objects and let EnergyPlus ExpandObjects
 * build the AirLoop / PlantLoop / node networks — sound IDF without exposing raw
 * complexity. Core invariant enforced everywhere: ONE zone → ONE HVAC system.
 *
 * System shape:
 *   { id, type, name,
 *     targets: [{ kind:'zone'|'group', id }],     // served zones (union, no overlap across systems)
 *     thermostat: { heating_setpoint, cooling_setpoint },
 *     availability: 'always_on' | <schedule>,
 *     sizing: { heating_factor, cooling_factor,
 *               cooling_capacity:'autosize'|number, heating_capacity:'autosize'|number },
 *     params: { …type-specific… } }
 */

import { polygonArea } from './geometry.js';
import { resolveSchedule, TEMPLATES } from './scheduleTemplates.js';

let _seq = 0;
const uid = () => `hvac_${Date.now().toString(36)}_${(_seq++).toString(36)}`;

// ── Reusable plant-input blocks ───────────────────────────────────────────────
// Systems with a central chiller and/or boiler share these grouped Details inputs.
// Cooling (chilled water) is intrinsic to VAV / fan-coil, so the cooling toggle is
// shown but locked on; heating (boiler) is genuinely optional via its toggle.
// Order per request: chiller type → capacity → COP → condenser, then CHW-loop;
// boiler type → capacity → efficiency → fuel, then HW-loop.
function coolingPlantParams(over = {}) {
  return [
    { key: 'has_cooling', label: 'Chilled-water cooling (required)', type: 'toggle', default: true, disabled: true, group: 'Cooling — Chiller' },
    { key: 'chiller_equipment', label: 'Chiller Type', type: 'select', default: over.chiller_equipment ?? 'ElectricScrewChiller', group: 'Cooling — Chiller',
      options: [{ value: 'ElectricScrewChiller', label: 'Electric Screw' }, { value: 'ElectricCentrifugalChiller', label: 'Electric Centrifugal' }, { value: 'ElectricReciprocatingChiller', label: 'Electric Reciprocating' }, { value: 'DistrictChilledWater', label: 'District Chilled Water' }] },
    { key: 'chiller_capacity', label: 'Chiller Capacity (W, blank = autosize)', default: '', min: 0, step: 1000, group: 'Cooling — Chiller' },
    { key: 'chiller_cop', label: 'Chiller COP', unit: 'W/W', default: over.chiller_cop ?? 5.0, min: 2, max: 8, step: 0.1, group: 'Cooling — Chiller' },
    { key: 'condenser_type', label: 'Condenser Type', type: 'select', default: 'WaterCooled', group: 'Cooling — Chiller',
      options: [{ value: 'WaterCooled', label: 'Water-Cooled (+ cooling tower)' }, { value: 'AirCooled', label: 'Air-Cooled' }, { value: 'EvaporativelyCooled', label: 'Evaporatively-Cooled' }] },
    { key: 'chw_setpoint', label: 'Chilled-Water Design Setpoint', unit: '°C', default: 6.7, min: 4, max: 10, step: 0.1, group: 'Cooling — Chilled-Water Loop' },
    { key: 'chw_pump_control', label: 'Pump Control Type', type: 'select', default: 'Intermittent', group: 'Cooling — Chilled-Water Loop',
      options: [{ value: 'Intermittent', label: 'Intermittent (cycles with load)' }, { value: 'Continuous', label: 'Continuous' }] },
    { key: 'chw_pump_config', label: 'Pump Configuration', type: 'select', default: 'ConstantPrimaryNoSecondary', group: 'Cooling — Chilled-Water Loop',
      options: [{ value: 'ConstantPrimaryNoSecondary', label: 'Constant primary (no secondary)' }, { value: 'VariablePrimaryNoSecondary', label: 'Variable primary (no secondary)' }, { value: 'ConstantPrimaryVariableSecondary', label: 'Constant primary + variable secondary' }] },
  ];
}
function heatingPlantParams(over = {}) {
  const show = (p) => !!p.has_heating;
  return [
    { key: 'has_heating', label: 'Hot-water heating plant', type: 'toggle', default: over.has_heating ?? true, group: 'Heating — Boiler' },
    { key: 'boiler_type', label: 'Boiler Type', type: 'select', default: 'HotWaterBoiler', group: 'Heating — Boiler', showIf: show,
      options: [{ value: 'HotWaterBoiler', label: 'Hot-Water Boiler' }, { value: 'CondensingHotWaterBoiler', label: 'Condensing Boiler' }, { value: 'DistrictHotWater', label: 'District Hot Water' }] },
    { key: 'boiler_capacity', label: 'Boiler Capacity (W, blank = autosize)', default: '', min: 0, step: 1000, group: 'Heating — Boiler', showIf: show },
    { key: 'boiler_eff', label: 'Boiler Efficiency (0–1)', unit: '', default: over.boiler_eff ?? 0.8, min: 0.6, max: 1, step: 0.01, group: 'Heating — Boiler', showIf: show },
    { key: 'boiler_fuel', label: 'Boiler Fuel', type: 'select', default: 'NaturalGas', group: 'Heating — Boiler', showIf: show,
      options: [{ value: 'NaturalGas', label: 'Natural Gas' }, { value: 'Electricity', label: 'Electricity' }, { value: 'Propane', label: 'Propane' }, { value: 'FuelOilNo1', label: 'Fuel Oil No. 1' }, { value: 'FuelOilNo2', label: 'Fuel Oil No. 2' }] },
    { key: 'hw_setpoint', label: 'Hot-Water Design Setpoint', unit: '°C', default: 82, min: 50, max: 95, step: 1, group: 'Heating — Hot-Water Loop', showIf: show },
    { key: 'hw_pump_control', label: 'Pump Control Type', type: 'select', default: 'Intermittent', group: 'Heating — Hot-Water Loop', showIf: show,
      options: [{ value: 'Intermittent', label: 'Intermittent (cycles with load)' }, { value: 'Continuous', label: 'Continuous' }] },
    { key: 'hw_pump_config', label: 'Pump Configuration', type: 'select', default: 'ConstantFlow', group: 'Heating — Hot-Water Loop', showIf: show,
      options: [{ value: 'ConstantFlow', label: 'Constant flow' }, { value: 'VariableFlow', label: 'Variable flow' }] },
  ];
}

// DOAS add-on: the toggle plus its supply-fan flow (blank = autosize) and ΔP, shown
// only when DOAS is enabled. Used by the zonal systems (PTAC / VRF / Fan Coil).
function doasParams(defaultOn = false) {
  const show = (p) => p.doas === 'Yes';
  return [
    { key: 'doas', label: 'Dedicated Outdoor Air (DOAS)', type: 'select', default: defaultOn ? 'Yes' : 'No', group: 'Ventilation',
      options: [{ value: 'No', label: 'No' }, { value: 'Yes', label: 'Yes — add a DOAS for ventilation' }] },
    { key: 'doas_flow', label: 'DOAS Supply-Fan Flow Rate (m³/s, blank = autosize)', default: '', min: 0, step: 0.05, group: 'Ventilation', showIf: show },
    { key: 'doas_fan_pressure', label: 'DOAS Supply-Fan Pressure Rise', unit: 'Pa', default: 1000, min: 100, max: 2500, step: 25, group: 'Ventilation', showIf: show },
  ];
}

// ── System type registry ──────────────────────────────────────────────────────
export const HVAC_TYPES = {
  ideal_loads: {
    id: 'ideal_loads', label: 'Ideal Loads Air System', level: 1,
    zoneObject: 'HVACTemplate:Zone:IdealLoadsAirSystem',
    systemObject: null, plant: false,
    blurb: 'Perfect, unlimited-capacity conditioning — the standard choice for envelope-compliance studies.',
    params: [],
  },
  ptac: {
    id: 'ptac', label: 'Packaged Terminal AC (PTAC)', level: 2,
    zoneObject: 'HVACTemplate:Zone:PTAC',
    systemObject: null, plant: false, doasCapable: true,
    blurb: 'Through-the-wall packaged units, one per zone. Electric or gas heat.',
    params: [
      { key: 'cooling_cop',   label: 'Cooling COP',        unit: 'W/W', default: 3.0, min: 2, max: 6, step: 0.1 },
      { key: 'heating_type',  label: 'Heating Type',       type: 'select', default: 'Electric',
        options: [{ value: 'Electric', label: 'Electric' }, { value: 'Gas', label: 'Gas' }, { value: 'None', label: 'None (cooling only)' }] },
      { key: 'heating_eff',   label: 'Heating Efficiency (0–1)', unit: '',  default: 1.0, min: 0.6, max: 1, step: 0.01 },
      { key: 'fan_eff',       label: 'Fan Total Efficiency (0–1)', unit: '', default: 0.7, min: 0.3, max: 0.9, step: 0.05 },
      { key: 'fan_power',     label: 'Fan Pressure Rise',  unit: 'Pa', default: 75, min: 0, max: 600, step: 5 },
      { key: 'oa_method',     label: 'Outdoor Air Method',   type: 'select', default: 'Flow/Person', group: 'Ventilation',
        options: [
          { value: 'Flow/Person', label: 'Flow per Person' },
          { value: 'Flow/Area', label: 'Flow per Floor Area' },
          { value: 'Flow/Zone', label: 'Flow per Zone' },
          { value: 'DetailedSpecification', label: 'Detailed — use Loads-screen ventilation' },
        ] },
      { key: 'oa_rate',       label: 'OA Flow Rate (m³/s — per person / area / zone)', default: 0.00944, min: 0, max: 5, step: 0.0001, group: 'Ventilation' },
      ...doasParams(false),
    ],
  },
  vrf: {
    id: 'vrf', label: 'Variable Refrigerant Flow (VRF)', level: 3,
    zoneObject: 'HVACTemplate:Zone:VRF',
    systemObject: 'HVACTemplate:System:VRF', plant: false, doasCapable: true,
    // System-level capacity is defined for the whole outdoor unit (not per zone),
    // so the Sizing step shows a system card here instead of per-zone capacity.
    systemCapacity: true,
    // Per-zone inputs of HVACTemplate:Zone:VRF beyond the heating/cooling sizing
    // factors — rendered as extra columns in the per-zone Sizing matrix (each
    // terminal unit can differ). Supply Fan ΔP is field 24 of HVACTemplate:Zone:VRF.
    zoneFields: [
      { key: 'fan_pressure', label: 'Supply Fan ΔP', unit: 'Pa', default: 75, min: 0, max: 600, step: 5 },
    ],
    blurb: 'One outdoor unit per system with indoor terminal units auto-generated per served zone. High part-load efficiency.',
    params: [
      // section:'sizing' → shown in the Sizing step's system card (with the gross
      // capacities), not the Details step. COP is an outdoor-unit (system) rating.
      { key: 'cooling_cop', label: 'Cooling COP', unit: 'W/W', default: 3.3, min: 2, max: 7, step: 0.1, section: 'sizing' },
      { key: 'heating_cop', label: 'Heating COP', unit: 'W/W', default: 3.4, min: 2, max: 7, step: 0.1, section: 'sizing' },
      // Heat recovery is reserved in HVACTemplate:System:VRF (No only) — to model it
      // we'd emit detailed AirConditioner:VariableRefrigerantFlow objects (future).
      { key: 'oa_method',   label: 'Outdoor Air Method',   type: 'select', default: 'Flow/Person', group: 'Ventilation',
        options: [
          { value: 'Flow/Person', label: 'Flow per Person' },
          { value: 'Flow/Area', label: 'Flow per Floor Area' },
          { value: 'Flow/Zone', label: 'Flow per Zone' },
          { value: 'DetailedSpecification', label: 'Detailed — use Loads-screen ventilation' },
        ] },
      { key: 'oa_rate',     label: 'OA Flow Rate (m³/s — per person / area / zone)', default: 0.00944, min: 0, max: 5, step: 0.0001, group: 'Ventilation' },
      { key: 'doas', label: 'Dedicated Outdoor Air (DOAS)', type: 'select', default: 'No', group: 'Ventilation',
        options: [{ value: 'No', label: 'No' }, { value: 'Yes', label: 'Yes — add a DOAS for ventilation' }] },
    ],
  },
  unitary: {
    id: 'unitary', label: 'Unitary System (packaged air loop)', level: 2,
    zoneObject: 'HVACTemplate:Zone:Unitary',
    systemObject: 'HVACTemplate:System:UnitarySystem', plant: false, airLoop: true,
    blurb: 'A packaged air loop (DX cooling + heating coil + fan) serving its zones through ductwork.',
    params: [
      { key: 'cooling_cop',  label: 'Cooling COP',         unit: 'W/W', default: 3.0, min: 2, max: 6, step: 0.1 },
      { key: 'heating_type', label: 'Heating Coil',        type: 'select', default: 'Electric',
        options: [{ value: 'Electric', label: 'Electric' }, { value: 'Gas', label: 'Gas' }] },
      { key: 'heating_eff',  label: 'Heating Efficiency (0–1)',  unit: '',  default: 0.8, min: 0.6, max: 1, step: 0.01 },
      { key: 'fan_eff',      label: 'Fan Total Efficiency (0–1)', unit: '', default: 0.7, min: 0.3, max: 0.9, step: 0.05 },
      { key: 'fan_pressure', label: 'Fan Pressure Rise',   unit: 'Pa', default: 600, min: 100, max: 2000, step: 25 },
    ],
  },
  vav: {
    id: 'vav', label: 'Variable Air Volume (VAV)', level: 5,
    zoneObject: 'HVACTemplate:Zone:VAV',
    systemObject: 'HVACTemplate:System:VAV', plant: true, airLoop: true,
    // Cooling is delivered by the central chilled-water coil (chiller autosizes), so
    // there is no per-zone coil capacity to enter — the Sizing matrix shows only the
    // per-zone heating/cooling sizing factors.
    noZoneCapacity: true,
    blurb: 'Central variable-air-volume air handler on a chilled-water plant (chiller + cooling tower), with a reheat coil at each VAV box.',
    params: [
      { key: 'fan_eff',            label: 'Supply Fan Total Efficiency (0–1)', unit: '', default: 0.7, min: 0.3, max: 0.9, step: 0.05 },
      { key: 'fan_pressure',       label: 'Supply Fan Pressure Rise',    unit: 'Pa', default: 959, min: 100, max: 2500, step: 25 },
      { key: 'cooling_setpoint',   label: 'Cooling Supply-Air Temp',     unit: '°C', default: 12.8, min: 8, max: 18, step: 0.1 },
      { key: 'economizer',         label: 'Air-Side Economizer',         type: 'select', default: 'FixedDryBulb',
        options: [{ value: 'FixedDryBulb', label: 'Fixed Dry-Bulb' }, { value: 'DifferentialDryBulb', label: 'Differential Dry-Bulb' }, { value: 'NoEconomizer', label: 'None' }] },
      { key: 'heat_recovery',      label: 'Heat Recovery',               type: 'select', default: 'Enthalpy',
        options: [{ value: 'Enthalpy', label: 'Enthalpy (total)' }, { value: 'Sensible', label: 'Sensible' }, { value: 'None', label: 'None' }] },
      { key: 'return_fan',         label: 'Return Fan',                  type: 'select', default: 'Yes',
        options: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] },
      ...coolingPlantParams({ chiller_cop: 5.0 }),
      ...heatingPlantParams({ has_heating: true }),
    ],
  },
  packaged_vav: {
    id: 'packaged_vav', label: 'Packaged VAV (rooftop DX)', level: 4,
    zoneObject: 'HVACTemplate:Zone:VAV',
    systemObject: 'HVACTemplate:System:PackagedVAV', plant: false, airLoop: true,
    // The packaged unit has a single DX cooling coil sized for the whole air loop —
    // a system-level gross capacity, not per-zone. Heating is at the VAV boxes, so
    // there is no system-level heating capacity to enter.
    systemCapacity: true, coolingOnlyCapacity: true,
    blurb: 'A packaged rooftop variable-air-volume unit with a two-speed DX cooling coil, serving its zones through VAV boxes with reheat. No chilled-water plant.',
    params: [
      { key: 'cooling_cop',      label: 'Cooling COP',                 unit: 'W/W', default: 3.0, min: 2, max: 6, step: 0.1, section: 'sizing' },
      { key: 'fan_eff',          label: 'Supply Fan Total Efficiency (0–1)', unit: '', default: 0.7, min: 0.3, max: 0.9, step: 0.05 },
      { key: 'fan_pressure',     label: 'Supply Fan Pressure Rise',    unit: 'Pa', default: 1000, min: 100, max: 2500, step: 25 },
      { key: 'cooling_setpoint', label: 'Cooling Supply-Air Temp',     unit: '°C', default: 12.8, min: 8, max: 18, step: 0.1 },
      { key: 'economizer',       label: 'Air-Side Economizer',         type: 'select', default: 'NoEconomizer',
        options: [{ value: 'NoEconomizer', label: 'None' }, { value: 'FixedDryBulb', label: 'Fixed Dry-Bulb' }, { value: 'DifferentialDryBulb', label: 'Differential Dry-Bulb' }] },
      { key: 'heat_recovery',    label: 'Heat Recovery',               type: 'select', default: 'None',
        options: [{ value: 'None', label: 'None' }, { value: 'Sensible', label: 'Sensible' }, { value: 'Enthalpy', label: 'Enthalpy (total)' }] },
      // DX cooling (no chiller plant). Heating (HW reheat boiler) optional, default off.
      ...heatingPlantParams({ has_heating: false }),
    ],
  },
  fan_coil: {
    id: 'fan_coil', label: 'Fan Coil Units + DOAS', level: 4,
    zoneObject: 'HVACTemplate:Zone:FanCoil',
    systemObject: null, plant: true, doasCapable: true,
    blurb: 'Water-based fan coils on a central chilled-water (and optional hot-water) plant, with a dedicated outdoor-air system for ventilation.',
    params: [
      { key: 'fan_eff',       label: 'Fan Total Efficiency (0–1)', unit: '', default: 0.7, min: 0.3, max: 0.9, step: 0.05 },
      { key: 'fan_pressure',  label: 'Fan Pressure Rise',    unit: 'Pa', default: 75, min: 25, max: 1000, step: 25 },
      ...coolingPlantParams({ chiller_cop: 5.5 }),
      ...heatingPlantParams({ has_heating: true, boiler_eff: 0.85 }),
      { key: 'oa_method',     label: 'Outdoor Air Method',   type: 'select', default: 'Flow/Person', group: 'Ventilation',
        options: [
          { value: 'Flow/Person', label: 'Flow per Person' },
          { value: 'Flow/Area', label: 'Flow per Floor Area' },
          { value: 'Flow/Zone', label: 'Flow per Zone' },
          { value: 'DetailedSpecification', label: 'Detailed — use Loads-screen ventilation' },
        ] },
      { key: 'oa_rate',       label: 'OA Flow Rate (m³/s — per person / area / zone)', default: 0.00944, min: 0, max: 5, step: 0.0001, group: 'Ventilation' },
      ...doasParams(true),
    ],
  },
};

export const HVAC_TYPE_ORDER = ['ideal_loads', 'ptac', 'vrf', 'vav', 'packaged_vav', 'fan_coil'];

export const getHvacType = (type) => HVAC_TYPES[type] || null;
export const hvacParams = (type) => HVAC_TYPES[type]?.params || [];

// Project Setup's primary_hvac_template → a sensible default system type for "Add".
export const TEMPLATE_TO_TYPE = {
  ptac: 'ptac', split: 'ptac', vrf: 'vrf',
  chw_vav_water: 'vav', chw_vav_air: 'packaged_vav',
};

// Thermostat: each of heating/cooling is either a Schedules-screen setpoint schedule
// ('schedule', the default) or a user-entered constant ('fixed').
export const DEFAULT_THERMOSTAT = {
  heating_mode: 'schedule', heating_setpoint: 21,
  cooling_mode: 'schedule', cooling_setpoint: 24,
};

export function createHvacSystem(type, overrides = {}) {
  const t = HVAC_TYPES[type];
  if (!t) throw new Error(`Unknown HVAC system type: ${type}`);
  const params = {};
  for (const p of t.params) params[p.key] = p.default;
  return {
    id: uid(),
    type,
    name: '',
    targets: [],
    thermostat: { ...DEFAULT_THERMOSTAT },
    availability: 'always_on',
    sizing: {
      heating_factor: 1,
      cooling_factor: 1,
      cooling_capacity: 'autosize',
      heating_capacity: 'autosize',
    },
    // Per-zone overrides of sizing + thermostat, keyed by zoneId. Absent zones fall
    // back to the system defaults above. Edited on the Sizing / Thermostat steps.
    zoneConfig: {},
    params,
    ...overrides,
  };
}

/** Merged per-zone config: system defaults overlaid with any zone-specific override. */
export function zoneCfg(system, zoneId) {
  const sz = system.sizing || {};
  const ts = system.thermostat || {};
  return {
    heating_factor:   sz.heating_factor ?? 1,
    cooling_factor:   sz.cooling_factor ?? 1,
    cooling_capacity: sz.cooling_capacity ?? 'autosize',
    heating_capacity: sz.heating_capacity ?? 'autosize',
    fan_pressure:     sz.fan_pressure ?? 75,   // per-zone supply-fan ΔP (Zone:VRF/FanCoil)
    heating_mode:     ts.heating_mode ?? 'schedule',
    heating_setpoint: ts.heating_setpoint ?? 21,
    cooling_mode:     ts.cooling_mode ?? 'schedule',
    cooling_setpoint: ts.cooling_setpoint ?? 24,
    ...((system.zoneConfig || {})[zoneId] || {}),
  };
}

/** Backfill any params/sizing/thermostat fields added after a system was created. */
export function withHvacDefaults(sys) {
  const t = HVAC_TYPES[sys.type];
  if (!t) return sys;
  const params = { ...sys.params };
  for (const p of t.params) if (params[p.key] == null) params[p.key] = p.default;
  return {
    ...sys,
    thermostat: { ...DEFAULT_THERMOSTAT, ...(sys.thermostat || {}) },
    sizing: { heating_factor: 1, cooling_factor: 1, cooling_capacity: 'autosize', heating_capacity: 'autosize', ...(sys.sizing || {}) },
    zoneConfig: sys.zoneConfig && typeof sys.zoneConfig === 'object' ? sys.zoneConfig : {},
    targets: Array.isArray(sys.targets) ? sys.targets : [],
    params,
  };
}

// ── ECSBC 2024 §12 Table 12.2 — Baseline HVAC system map ──────────────────────
// System A: Split AC (hotel/hospital rooms, resorts, villas, sleeping quarters,
//   schools, classrooms). System B: VRF (≤ 12,500 m² conditioned). System C:
//   Central CHW plant + VAV (> 12,500 m²). System D: Computer-room AC (data centres).
// Each maps to the closest system type in this app's registry.
const SYSTEM_A_BUILDING_TYPES = ['hospital', 'hospitality', 'residential', 'school'];

export function ecsbcBaselineApplicability(building) {
  const bt = building.building_type;
  const cat = building.building_category;
  const condArea = (building.floors || [])
    .flatMap((f) => f.zones || [])
    .filter((z) => z.is_conditioned)
    .reduce((s, z) => s + Math.abs(polygonArea(z.footprint || [])), 0);
  const isA = SYSTEM_A_BUILDING_TYPES.includes(bt) || cat === 'residential';
  const isData = bt === 'data_center';
  const big = condArea > 12500;
  return {
    condArea,
    systems: [
      { key: 'A', name: 'System A', desc: 'Split AC', type: 'ptac',     applicable: isA,
        reason: 'Hotel/hospital rooms, residential, schools' },
      { key: 'B', name: 'System B', desc: 'VRF',       type: 'vrf',      applicable: !isA && !isData && !big,
        reason: '≤ 12,500 m² conditioned area' },
      { key: 'C', name: 'System C', desc: 'Central CHW plant + VAV', type: 'vav', applicable: !isA && !isData && big,
        reason: '> 12,500 m² conditioned area' },
      { key: 'D', name: 'System D', desc: 'Computer-room AC', type: 'unitary', applicable: isData,
        reason: 'Data centre / server / computer rooms' },
    ],
  };
}

// ── Thermostat setpoint schedules (from the Schedules screen, building level) ──
const tmplLabel = (t) => TEMPLATES[t]?.label || (t === 'custom' ? 'Custom' : 'Default');
export function setpointScheduleInfo(building) {
  const bt = building.building_type || 'office';
  const code = building.schedule_code;
  const S = building.schedules || {};
  const clg = resolveSchedule('cooling_sp', S.building, {}, {}, bt, code);
  const htg = resolveSchedule('heating_sp', S.building, {}, {}, bt, code);
  return {
    cooling: { profile: clg, label: tmplLabel(clg.template) },
    heating: { profile: htg, label: tmplLabel(htg.template) },
  };
}

// ── Display helpers ───────────────────────────────────────────────────────────
export function systemTypeLabel(type) { return HVAC_TYPES[type]?.label || type; }

/** Short capacity descriptor for cards. */
export function capacityLabel(sys) {
  const c = sys.sizing?.cooling_capacity;
  return c === 'autosize' || c == null ? 'AutoSized' : `${c} W`;
}
